import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import PlatformService, { PlatformData } from "../../services/Platform";
import { useUserSession } from "../UserSessionContext";
import { logAndFormatFormError } from "../../utils/Errors";
import { displayToastMessage } from "../../utils/Toasts";
import platformConfig from "../../config/platforms";

interface PlatformContextType {
  platforms: PlatformData[];
  isLoading: boolean;
  error: string | null;
  refreshPlatforms: () => Promise<void>;
  addPlatform: (
    name: string,
    currentPlan: string,
    currentCost: number
  ) => Promise<boolean>;
  hasPlatforms: boolean;
  getPlatforms: () => PlatformData[]; // Added getter function
}

// Create the context
const PlatformContext = createContext<PlatformContextType | undefined>(
  undefined
);

// Provider component
export const PlatformProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [platforms, setPlatforms] = useState<PlatformData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { userSession } = useUserSession();
  const userIdRef = useRef<string | null>(null);

  // Update reference when userSession changes
  useEffect(() => {
    if (userSession?.id) {
      userIdRef.current = userSession.id;
    } else {
      userIdRef.current = null;
    }
  }, [userSession]);

  // Fetch platforms using the current userId from ref
  const refreshPlatforms = useCallback(async () => {
    const userId = userIdRef.current;
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const platformService = new PlatformService();
      const resData = await platformService.getAllPlatforms(userId);
      const fetchedPlatforms = resData?.platforms || [];

      setPlatforms(fetchedPlatforms);
    } catch (error: any) {
      const errorMessage = logAndFormatFormError(
        "PlatformContext.refreshPlatforms",
        error,
        "Fetching platforms"
      );
      setError(errorMessage);
      displayToastMessage("error", errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies, uses refs instead

  // Add new platform(s)
  const addPlatform = useCallback(
    async (name: string, currentPlan: string, currentCost: number) => {
      const userId = userIdRef.current;
      if (!userId || !platformConfig[name]) return false;

      try {
        const platformService = new PlatformService();
        await platformService.batchCreatePlatforms(
          [
            {
              name: name,
              currentPlan,
              currentCost,
            },
          ],
          userId
        );

        // Refresh the platform list
        await refreshPlatforms();

        displayToastMessage("success", "Platforms connected successfully!");
        return true;
      } catch (error: any) {
        const errorMessage = logAndFormatFormError(
          "PlatformContext.addPlatform",
          error,
          "Adding platforms"
        );
        displayToastMessage("error", errorMessage);
        return false;
      }
    },
    [refreshPlatforms]
  );

  // Getter function for platforms
  const getPlatforms = useCallback(() => {
    return platforms;
  }, [platforms]);

  // Load platforms initially and when userSession changes
  useEffect(() => {
    if (userSession?.id) {
      refreshPlatforms();
    }
  }, [userSession, refreshPlatforms]); // Only depend on userSession, not refreshPlatforms

  const value = {
    platforms,
    isLoading,
    error,
    refreshPlatforms,
    addPlatform,
    hasPlatforms: platforms.length > 0,
    getPlatforms,
  };

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
};

// Hook for using the platform context
export const usePlatforms = () => {
  const context = useContext(PlatformContext);
  if (context === undefined) {
    throw new Error("usePlatforms must be used within a PlatformProvider");
  }
  return context;
};
